import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Truck, Plane, Zap, Anchor, CircuitBoard, Building2, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

const EglantineProfile = () => {
  const user = {
    name: "Eglantine Pauvarel Moss",
    role: "Transportation, Maritime, and ICT Commercial Attaché",
    email: "Eglantine.PauvarelMoss@mri.gouv.qc.ca",
    solutionBounties: 5,
    domains: [
      "Transportation Systems",
      "Maritime Technology",
      "ICT Innovation",
      "Electric Vehicles",
      "Smart Logistics",
      "Port Infrastructure",
      "Digital Health",
      "Sustainable Mobility",
      "Supply Chain Management"
    ],
    industries: [
      { name: "Transportation", icon: Truck },
      { name: "Maritime", icon: Anchor },
      { name: "Aerospace", icon: Plane },
      { name: "ICT", icon: CircuitBoard },
      { name: "Government", icon: Building2 },
      { name: "Clean Energy", icon: Zap }
    ],
    confirmedSolutions: 12,
    confirmedKnowledgeBases: 28,
    tokenBalance: 95,
    bio: "Eglantine Pauvarel Moss serves as Commercial Attaché for Transportation, Maritime, and ICT sectors at the Quebec Government Office in Houston. She specializes in advancing Quebec's transportation, maritime, and information technology sectors within the Southern United States. Her expertise encompasses electric and sustainable transportation solutions, smart maritime logistics, and digital innovation. Eglantine works to foster commercial relationships, identify market opportunities, and facilitate technology transfer between Quebec companies and American partners in these strategic sectors."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/90311c01-1060-4ba9-9168-803118931ae9.png"
                    imageAlt="Eglantine Pauvarel Moss"
                  >
                    <Avatar className="h-36 w-36 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/90311c01-1060-4ba9-9168-803118931ae9.png" 
                        alt="Eglantine Pauvarel Moss"
                        className="object-cover object-center image-rendering-auto"
                        style={{ imageRendering: 'crisp-edges' }}
                      />
                      <AvatarFallback>EP</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100 hover-scale"
                        >
                          BIO
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-[500px] p-4 animate-fade-in">
                        <div className="space-y-3">
                          <h4 className="text-lg font-semibold">Eglantine Pauvarel Moss</h4>
                          <div className="text-sm text-gray-700 space-y-2">
                            <p>Eglantine Pauvarel Moss is the Transportation Commercial Attaché for the Québec Government Office in Houston, where she leads international business development efforts across the transportation, maritime, and ICT sectors. In this role, she promotes Québec's strategic economic interests across Texas, Louisiana, Oklahoma, and Arkansas—facilitating cross-border partnerships, investment opportunities, and innovation diplomacy aligned with Québec's core priorities in clean technology, infrastructure, and sustainable mobility.</p>
                            <p>Before joining the Québec Government, Eglantine was the founding attorney of Pauvarel Moss Law Firm, PLLC, a Houston-based boutique firm specializing in immigration and nationality law. Over seven years, she built a global client base supporting individuals, families, and companies navigating the complex U.S. immigration landscape with integrity and impact.</p>
                            <p>Eglantine's career reflects deep international engagement, with previous leadership roles including Executive Director of the French-American Chamber of Commerce in Texas and legal analyst for the European Commission. She also worked on transatlantic diplomacy initiatives, conference strategy, and legal harmonization programs at institutions like the International Civil Aviation Organization and the University of Houston Law Center.</p>
                            <p>She holds a Juris Doctor from the University of Houston Law Center, a Master's in European Law from Aix-Marseille University, and degrees in International Relations and Comparative Law from Sciences Po Aix and the Université de Grenoble. She also completed a political science exchange program at Louisiana State University—strengthening her transatlantic perspective and regional understanding of the southern U.S.</p>
                            <p>Multilingual, deadline-oriented, and mission-driven, Eglantine brings a rare combination of legal expertise, diplomatic experience, and commercial insight to Québec's international economic efforts.</p>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100 hover-scale"
                        >
                          NW
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-96 p-4 animate-fade-in">
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold">Eglantine's Knowledge Network</h4>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/ozlem-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/a044a687-d14a-497e-9bb5-eec5d53ab9aa.png" alt="Ozlem" className="object-cover" />
                                  <AvatarFallback>OT</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Ozlem</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/karen-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/1747ca6f-b961-4ea7-bae4-a5d133e2a21b.png" alt="Karen" />
                                  <AvatarFallback>KA</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Karen</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/pete-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/75937035-16a7-4dc5-88cc-58dd952f6c60.png" alt="Pete" />
                                  <AvatarFallback>PE</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Pete</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/trey-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/34971d27-7908-4c61-ac9e-881493ec551d.png" alt="Trey" />
                                  <AvatarFallback>TR</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Trey</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                              <Link to="/steven-profile">
                                <Avatar className="w-16 h-16 border-2 border-primary transition-all duration-300 hover:scale-110 cursor-pointer">
                                  <AvatarImage src="/lovable-uploads/10314fa3-311f-4fa5-af44-1e79e6e66f43.png" alt="Steven" className="object-cover" />
                                  <AvatarFallback>SJ</AvatarFallback>
                                </Avatar>
                              </Link>
                              <span className="text-sm font-medium">Steven</span>
                            </div>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ImageHoverPopup 
                      imageSrc="/lovable-uploads/90311c01-1060-4ba9-9168-803118931ae9.png"
                      imageAlt="Eglantine Pauvarel Moss"
                    >
                      <h2 className="font-serif text-3xl font-bold text-black cursor-pointer hover:text-primary transition-colors">{user.name}</h2>
                    </ImageHoverPopup>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                  </div>
                  <div className="flex items-center mb-4">
                    <p className="text-lg font-medium text-black">{user.role}</p>
                    <Link to="/quebec-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d41ea10b-9167-451e-944e-ce5900958bf1.png" 
                        alt="Quebec Economic Development" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link
                          to="/solution-bounties/eglantine"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={8.7} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge
                    key={domain}
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EglantineProfile;